import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Box,
  Button,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  useTheme,
  useMediaQuery,
  Grid,
  CircularProgress,
  Typography,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { Link, useNavigate } from "react-router-dom";
import { MoreVert } from "@mui/icons-material";
import { Search, X } from "lucide-react";
import { useSelector } from "react-redux";
import { PATHOLOGY_LIST } from "../_mock/mock";
import { useGetApi } from "../hooks/useGetApi";
import {
  getPathologyApi,
  updatePathologyApi,
  deletePathologyByIdApi,
} from "../services/pathology.service";
import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";
import dayjs from "dayjs";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import { toast } from "react-toastify";
import { hasPermission } from "../utils/permissionHelper";

const headLabel = [
  {
    id: 1,
    label: "Date",
    value: "date",
  },
  {
    id: 2,
    label: "Test Name",
    value: "testName",
  },
  {
    id: 2,
    label: "UHID",
    value: "uhid",
  },
  {
    id: 3,
    label: "Name",
    value: "name",
  },
  {
    id: 4,
    label: "Ward Name",
    value: "wardName",
  },
  {
    id: 5,
    label: "Bed Number",
    value: "bedNumber",
  },
  {
    id: 6,
    label: "Doctor Name",
    value: "doctorName",
  },
  {
    id: 7,
    label: "Status",
    value: "status",
  },
  {
    id: 8,
    label: "",
    value: "",
    align: "right",
  },
];

const popoverOptions = ["Cancel Report", "Delete"];

const buttons = [
  { label: "All Reports", width: "100px" },
  { label: "Pending", width: "100px" },
  { label: "Accepted and Generated", width: "160px" },
  { label: "Sample Collected", width: "130px" },
  { label: "In Progress", width: "100px" },
  { label: "Dr Approval Pending", width: "130px" },
  { label: "Dispatched", width: "100px" },
  { label: "Cancelled", width: "100px" },
];

const Pathology = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_LIMIT });
  const [sortModel, setSortModel] = useState([]);
  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("DATE");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchContainerRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const [anchorPosition, setAnchorPosition] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [curPatientId, setCurPatientId] = useState("");
  const [activeButton, setActiveButton] = useState("All Reports");
  const [dateFilter, setDateFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const user = useSelector((state) => state.user);
  
  // Filter popover options based on permissions
  const getFilteredPopoverOptions = () => {
    let options = ["Cancel Report"];
    if (hasPermission(user, "Pathology.Delete")) {
      options.push("Delete");
    }
    return options;
  };

  const {
    dataList: pathologyList,
    dataCount: pathologyCount,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: getPathologyApi,
    params: {
      limit: 10000,
      page: 1, // eslint-disable-line no-undef
      search,
      searchBy,
      dateFilter,
      status: activeButton === "All Reports" ? "" : activeButton,
    },
    dependencies: [search, searchBy, activeButton, dateFilter],
    debounceDelay: 500,
    dataKey: "pathologyRequests",
    countKey: "total",
  });

  const handleRightClick = (event, row) => {
    event.preventDefault(); // Prevent the default context menu
    setAnchorEl(event.currentTarget);
    setAnchorPosition({
      top: event.clientY,
      left: event.clientX,
    });
    setSelectedRow(row); // Set the clicked row data
    setCurPatientId(row?.patientId);
  };

  const handleClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setAnchorPosition(null);
    setSelectedRow(row); // Set the clicked row data
    setCurPatientId(row?.patientId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handlePaginationModelChange = (newModel) => setPaginationModel(newModel);
  const handleSortModelChange = (newSortModel) => setSortModel(newSortModel);

  const cancelReportHandler = async () => {
    if (
      selectedRow?.status === "Dispatched" ||
      selectedRow?.status === "Cancelled"
    ) {
      toast.info("This report is already dispatched or cancelled");
      setSelectedRow(null);
      return;
    }
    let response;
    setLoading(true);
    response = await updatePathologyApi(selectedRow?._id, {
      status: "Cancelled",
    });
    setLoading(false);

    if (response.code === 200 || response.code === 201) {
      toast.success("Report cancelled successfully");
      setSelectedRow(null);
      refetch();
    } else {
      toast.error(response?.message || "Some error occurred.");
      setSelectedRow(null);
    }
  };

  const deleteReportHandler = async () => {
    if (!selectedRow?._id) {
      toast.error("No report selected");
      setSelectedRow(null);
      return;
    }

    let response;
    setLoading(true);
    response = await deletePathologyByIdApi(selectedRow?._id);
    setLoading(false);

    if (response.code === 200 || response.code === 201) {
      toast.success("Pathology request deleted successfully");
      setSelectedRow(null);
      refetch();
    } else {
      toast.error(response?.message || "Failed to delete pathology request");
      setSelectedRow(null);
    }
  };

  const handleMenuClick = (option) => {
    // Close popover immediately but preserve selectedRow for the operation
    setAnchorEl(null);
    
    if (option === "Cancel Report") {
      cancelReportHandler();
    } else if (option === "Delete") {
      deleteReportHandler();
    }
  };

  const rows = pathologyList?.map((row) => ({
    id: row._id,
    _id: row._id,
    date: row?.createdDate,
    testName: row?.service?.itemName ?? "—",
    UHID: row?.UHID ?? "—",
    name: row?.patient ? `${row.patient.firstName} ${row.patient.lastName}`.trim() : "—",
    wardName: row?.ward?.wardName ?? "—",
    bedNumber: row?.bedNumber?.bedNumber ?? "—",
    doctorName: row?.doctor ? `${row.doctor.firstName} ${row.doctor.lastName}` : "—",
    status: row?.status ?? "—",
    patientId: row?.patientId,
    service: row?.service,
    ward: row?.ward,
    bedNumberObj: row?.bedNumber,
    doctor: row?.doctor,
  })) ?? [];

  // Compute status counts when viewing all reports
  const statusCountsRef = useRef({});
  useMemo(() => {
    if (activeButton === "All Reports" && pathologyList?.length > 0) {
      const counts = { "All Reports": pathologyList.length };
      pathologyList.forEach((row) => {
        const s = row?.status;
        if (s) counts[s] = (counts[s] || 0) + 1;
      });
      statusCountsRef.current = counts;
    }
  }, [activeButton, pathologyList]);

  const getColumns = useCallback(() => {
    const baseColumns = [
      { field: "date", headerName: "Date", flex: isMobile ? 0 : 0.9, width: isMobile ? 100 : undefined, minWidth: 100, headerAlign: "center", align: "center", sortable: true, valueFormatter: (params) => (params.value ? dayjs(params.value).format("DD/MM/YYYY, hh:mm A") : "—") },
      { field: "testName", headerName: "Test Name", flex: isMobile ? 0 : 1, width: isMobile ? 120 : undefined, minWidth: 100, headerAlign: "center", align: "center", sortable: true, renderCell: (params) => (<Box component={params.row?.status !== "Cancelled" ? Link : "div"} to={params.row?.status !== "Cancelled" ? `/pathology/patient-progress/${params.row._id}` : undefined} sx={{ color: params.row?.status !== "Cancelled" ? "primary.main" : "text.disabled", cursor: params.row?.status !== "Cancelled" ? "pointer" : "default", textDecoration: params.value ? "underline" : "none", pointerEvents: params.row?.status === "Cancelled" ? "none" : "auto" }}>{params.value || "—"}</Box>) },
      { field: "UHID", headerName: "UHID", flex: isMobile ? 0 : 0.8, width: isMobile ? 90 : undefined, minWidth: 80, headerAlign: "center", align: "center", sortable: true },
      { field: "name", headerName: "Name", flex: isMobile ? 0 : 1, width: isMobile ? 110 : undefined, minWidth: 100, headerAlign: "center", align: "center", sortable: true },
      { field: "wardName", headerName: "Ward Name", flex: isTablet ? 0 : 0.8, width: isTablet ? 90 : undefined, minWidth: 80, headerAlign: "center", align: "center", sortable: true },
      { field: "bedNumber", headerName: "Bed Number", flex: isTablet ? 0 : 0.6, width: isTablet ? 90 : undefined, minWidth: 70, headerAlign: "center", align: "center", sortable: true },
      { field: "doctorName", headerName: "Doctor Name", flex: isTablet ? 0 : 0.9, width: isTablet ? 110 : undefined, minWidth: 100, headerAlign: "center", align: "center", sortable: true },
      { field: "status", headerName: "Status", flex: isTablet ? 0 : 0.8, width: isTablet ? 90 : undefined, minWidth: 80, headerAlign: "center", align: "center", sortable: true, renderCell: (params) => (<Box component="span" sx={{ color: params.value === "Pending" ? "rgba(218, 147, 4, 1)" : params.value === "Accept" || params.value === "Generated" ? "rgba(19, 162, 194, 1)" : params.value === "Cancelled" ? "rgba(224, 7, 0, 1)" : params.value === "Dr Approval" ? "rgba(222, 118, 0, 1)" : params.value === "Dispatched" ? "rgba(97, 173, 26, 1)" : params.value === "In Progress" ? "rgba(45, 35, 186, 1)" : "inherit" }}>{params.value || "—"}</Box>) },
      { field: "actions", headerName: "Actions", flex: 0, width: 60, minWidth: 50, headerAlign: "center", align: "center", sortable: false, renderCell: (params) => (<IconButton size={isMobile ? "small" : "medium"} onClick={(e) => { e.stopPropagation(); handleClick(e, params.row); }}><MoreVert sx={{ cursor: "pointer" }} /></IconButton>) },
    ];
    return baseColumns;
  }, [isMobile, isTablet]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ 
          width: "100%", 
          pr: { xs: "10px", sm: "20px", md: "40px", lg: "60px", xl: "90px" },
          pl: { xs: "10px", sm: "20px", md: "20px" },
          maxWidth: "100vw",
          overflow: "hidden"
        }}>
          {/* Search */}
          <Box
            sx={{
              paddingTop: "20px",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "20px",
            }}
          >
            <Box
              ref={searchContainerRef}
              sx={{
                display: "flex",
                backgroundColor: "white",
                border: isSearchFocused ? "2px solid #0179B0" : "1px solid #e0e0e0",
                borderRadius: "8px",
                alignItems: "center",
                position: "relative",
                boxShadow: isSearchFocused 
                  ? "0 4px 12px rgba(1, 121, 176, 0.15)" 
                  : "0 2px 4px rgba(0, 0, 0, 0.05)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  borderColor: "#c0c0c0"
                }
              }}
            >
              {/* Search Input */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                  width: "300px",
                  transition: "all 0.3s ease",
                  gap: "10px"
                }}
              >
                <Search 
                  size={20} 
                  style={{ 
                    color: isSearchFocused ? "#0179B0" : "#666",
                    transition: "color 0.3s ease",
                    flexShrink: 0
                  }} 
                />
                <input
                  type="text"
                  placeholder="Search by UHID"
                  value={search || ""}
                  onChange={(e) => setSearch(e.target.value || "")}
                  onFocus={() => setIsSearchFocused(true)}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    width: "100%",
                    fontSize: "14px",
                    color: "#333",
                    padding: "0",
                    minHeight: "24px",
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent"
                  }}
                />
                {search && (
                  <X 
                    size={18} 
                    style={{ 
                      cursor: "pointer", 
                      color: "#999",
                      flexShrink: 0,
                      padding: "2px",
                      transition: "all 0.2s ease",
                      touchAction: "manipulation"
                    }}
                    onClick={() => setSearch("")}
                    onMouseEnter={(e) => e.target.style.color = "#0179B0"}
                    onMouseLeave={(e) => e.target.style.color = "#999"}
                  />
                )}
              </Box>
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mb: "20px" }}>
            {buttons?.map((button, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Button
                  key={button?.label}
                  variant="outlined"
                  fullWidth
                  onClick={() => setActiveButton(button?.label)}
                  sx={{
                    border: `2px solid ${
                      activeButton === button?.label
                        ? theme.palette.primary.dark
                        : "#c0c0c0"
                    }`,
                    color:
                      activeButton === button?.label
                        ? theme.palette.primary.dark
                        : "text.secondary",
                    bgcolor:
                      activeButton === button?.label ? "#AED5F3" : "#F0F7FC",
                    fontWeight: activeButton === button?.label ? 600 : 400,
                    whiteSpace: "nowrap",
                    textTransform: "none",
                  }}
                >
                  {button?.label || ""}{statusCountsRef.current[button?.label] !== undefined ? ` (${statusCountsRef.current[button?.label]})` : ""}
                </Button>
              </Grid>
            ))}
          </Grid>

          {/* Filter */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <FormControl size="small" sx={{ minWidth: "100px" }}>
                <InputLabel id="date-select-label">Date</InputLabel>
                <Select
                  labelId="date-select-label"
                  label="Date"
                  value={dateFilter || ""}
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="yesterday">Yesterday</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
              {/* <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0 10px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                }}
              >
                <label htmlFor="uid-select">Visit Type: </label>
                <select className="uid-select" defaultValue={"IPD"}>
                  <option value="IPD">IPD</option>
                </select>
              </Box> */}
            </Box>
          </Box>
          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ height: { xs: "calc(100vh - 300px)", sm: "calc(100vh - 320px)", md: "calc(100vh - 350px)" }, width: "100%", minHeight: "300px", maxWidth: "100%", overflow: "hidden" }}>
                  <DataGrid
                    rows={rows}
                    columns={getColumns()}
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelChange}
                    pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                    paginationMode="client"
                    sortingMode="client"
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    disableRowSelectionOnClick
                    loading={isLoading}
                    sx={{
                      border: "none",
                      "& .MuiDataGrid-root": { border: "none", width: "100%" },
                      "& .MuiDataGrid-main": { width: "100%" },
                      "& .MuiDataGrid-cell": { borderBottom: "1px solid #f0f0f0", padding: isMobile ? "4px" : "8px", fontSize: isMobile ? "12px" : "14px" },
                      "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f5f5f5", borderBottom: "2px solid #e0e0e0" },
                      "& .MuiDataGrid-columnHeader": { backgroundColor: "#f5f5f5", fontWeight: "bold", fontSize: isMobile ? "12px" : "14px" },
                      "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold", fontSize: isMobile ? "12px" : "14px" },
                      "& .MuiDataGrid-row": { "&:hover": { backgroundColor: "#f5f5f5", cursor: "pointer" }, "&:nth-of-type(odd)": { backgroundColor: "#fafafa" }, "&:nth-of-type(even)": { backgroundColor: "white" } },
                      "& .MuiDataGrid-footerContainer": { borderTop: "2px solid #e0e0e0", backgroundColor: "#f9f9f9" },
                      "& .MuiDataGrid-virtualScroller": { minHeight: "200px" },
                      [theme.breakpoints.down("sm")]: { "& .MuiDataGrid-columnHeader": { fontSize: "11px", padding: "4px" }, "& .MuiDataGrid-cell": { fontSize: "11px", padding: "2px 4px" } },
                    }}
                    componentsProps={{ row: { onContextMenu: (event) => { event.preventDefault(); const rowId = event.currentTarget.getAttribute("data-id"); const rowData = rows.find((r) => String(r.id) === rowId); if (rowData) handleRightClick(event, rowData); } } }}
                    components={{ NoRowsOverlay: () => (<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}><Typography variant="body2" color="text.secondary">No pathology reports found</Typography></Box>) }}
                  />
                </Box>
              </Box>
            </>
          )}
          {/* Pop-up Menu */}

          <Menu
            id={Boolean(anchorEl) ? "row-popover" : undefined}
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
            anchorPosition={
              anchorPosition
                ? { top: anchorPosition.top, left: anchorPosition.left }
                : undefined
            }
            anchorOrigin={{
              vertical: "center",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: "8px",
                  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                  maxHeight: "300px",
                },
              },
            }}
          >
            {getFilteredPopoverOptions()?.map((option, index) => (
              <MenuItem key={index} onClick={() => handleMenuClick(option)}>
                {loading ? <CircularProgress size={20} /> : option || ""}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>
    </Container>
  );
};

export default Pathology;
